<template>
  <div class="animated fadeIn">
    <b-card>
      <div slot="header">
        <b-row>
          <b-col cols="6">
            <span v-if="isUpdateForm">{{ $t('resource.update_title') }}</span>
            <span v-if="noUIdFound || resDeleted">{{ $t('resource.no_resource_title') }}</span>
            <span v-if="!isUpdateForm & !noUIdFound & !resDeleted">{{ $t('resource.create_title') }}</span>
          </b-col>
        </b-row>
      </div>
      <div v-if="spinners.contentIsLoading" class="text-center">
        <b-spinner type="grow" variant="primary"/>
      </div>
      <div v-if="noUIdFound"> {{`UID: ${this.$route.params.uid} does not exist`}} </div>
      <div v-if="resDeleted"> {{`This resource with UID: ${this.$route.params.uid} is deleted`}} </div>
      <div v-if="!resDeleted & !noUIdFound">
        <b-form-group :invalid-feedback="fErr('name')">
          <label>{{ $t('resource.name') }}</label>
          <b-form-input type="text" :placeholder="$t('resource.name_placeholder')"
            :class="{ 'is-invalid' : fErr('name') }"
            v-model="entity.name"/>
        </b-form-group>
        <b-form-group :invalid-feedback="fErr('unique_id')">
          <label>{{ $t('resource.hr_unique_id') }}</label>
          <hex-input v-model="entity.unique_id" :placeholder="$t('resource.unique_id_placeholder')" 
            :class="{ 'is-invalid' : fErr('unique_id') }"/>
        </b-form-group>
        <b-form-group :invalid-feedback="fErr('person_responsible_id')">
          <label>{{ $t('resource.person_responsible_id') }}</label>
          <treeselect :class="{ 'treeselect-error' : fErr('person_responsible_id') }"
            :placeholder="$t('resource.person_responsible_id_placeholder')"
            :load-options="autocompleteUser" :async="true" :defaultOptions="true"
            v-model="entity.person_responsible_id"/>
        </b-form-group>
        <b-form-group :invalid-feedback="fErr('holder_name')">
          <label>{{ $t('resource.holder_name') }}</label>
          <b-form-input type="text" :placeholder="$t('resource.holder_name_placeholder')"
            :class="{ 'is-invalid' : fErr('holder_name') }"
            v-model="entity.holder_name"/>
        </b-form-group>
        <b-form-group :invalid-feedback="fErr('path')">
          <label>{{ $t('resource.path') }}</label>
          <b-form-input type="text" :placeholder="$t('resource.path_placeholder')"
            :class="{ 'is-invalid' : fErr('path') }"
            v-model="entity.path"/>
        </b-form-group>
      </div>
      <div v-if="!noUIdFound & !resDeleted" slot="footer">
        <b-button type="submit" variant="primary" :disabled="spinners.isSaving" @click="save()">
          <b-spinner v-if="spinners.isSaving" type="grow" small/>
          {{ $t('global.save') }}
        </b-button>
        <router-view :to="{ name: 'Resources' }">
          <b-button type="reset" variant="info" class="ml-2">
            {{ $t('global.cancel') }}
          </b-button>
        </router-view>
      </div>
    </b-card>
    <AuditLogs v-if="entity.id" :key="`raudit${entity.updated_at}`" :embedded="true" :subject_id="entity.id" :subject_type="'App\\Models\\Resource'" />
  </div>
</template>

<script>
import merge from 'lodash.merge';
import IdToHrHex from '@/shared/IdToHrHex';
import HrHexToId from '@/shared/HrHexToId';
import Services from '@/services/index';
import HexInput from '@/views/Shared/HexInput';
import AuditLogs from '@/views/Administration/AuditLogs';

import Treeselect, { ASYNC_SEARCH } from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';

export default {
  mounted() {
    this.load();
  },
  data() {
    return {
      noUIdFound: false,
      resDeleted: false,
      spinners: {
        contentIsLoading: false,
        isSaving: false,
      },
      entity: this.value,
      errorMsg: this.errors,
      hexid: null,
    };
  },
  props: {
    id: { type: Number, default: null },
    uid: { type: String, default: null },
    title: { type: [String, Boolean], default: undefined },
    errors: { type: Object, default() { return {}; } },
    // value is used both as DEFAULTs placeholder and for v-model
    value: {
      type: Object,
      default() {
        return {
          name: '',
          unique_id: null,
          person_responsible_id: null,
          holder_name: '',
          path: '',
        };
      },
    },
  },
  components: {
    Treeselect, HexInput, AuditLogs
  },
  computed: {
    isUpdateForm() {
      return !!this.entity.id;
    },
  },
  methods: {
    pushUpdate: Services.resources.update,
    pushCreate: Services.resources.create,
    IdToHrHex: IdToHrHex,

    load() {
      this.spinners.contentIsLoading = true;
      Services.resources.find({ resourceId: this.id, uniqueId: HrHexToId(this.uid) }).then((data) => {
        // Found UID matching a resource
        this.spinners.contentIsLoading = false;
        if (data.data.resource.deleted_at) {
          this.resDeleted = true;
          return null;
        }
        // merging entitiy with request data
        this.entity = { ...this.entity, ...(data.data.resource || {}) };
        this.entity.unique_id = this.entity.unique_id || HrHexToId(this.uid);
      }).catch((data) => {
        // No UID found
        if (data.status_code === 404) {
          this.noUIdFound = true;
          this.spinners.contentIsLoading = false;
        // No matching resource found with UID
        } else {
          this.spinners.contentIsLoading = false;
          this.errorMsg = data.data && data.data.data ? data.data.data.errors || {} : {};
          this.entity.unique_id = this.entity.unique_id || HrHexToId(this.uid);
        }
      });
      // });
    },
    save() {
      this.spinners.isSaving = true;
      const action = this.isUpdateForm ? this.pushUpdate : this.pushCreate;
      action(this.entity).then((data) => {
        this.spinners.isSaving = false;
        this.alertSuccess();
        merge(this.entity, data.data); // This will not delete keys but will preserve original object
        this.errorMsg = {};
        if (!this.isUpdateForm) {
          this.entity = { ...this.entity }; // clone object to force reactivity for isUpdateForm computed property
          this.$router.push({ name: 'ResourceUpdate', params: { id: data.data.id } });
        }
      }).catch(({ errors, message }) => { // Todo: Handle duplicate ID entry
        this.spinners.isSaving = false;
        this.errorMsg = errors;
        this.alertError({ text: message });
      });
    },
    autocompleteUser({ action, searchQuery, callback }) {
      if (action === ASYNC_SEARCH) {
        Services.users.list({ notpaginate: 1, search: searchQuery }).then(({ data }) => {
          const options = data.data.map((i) => ({
            id: i.id,
            label: `${i.id} ${i.name}`,
          }));
          callback(null, options);
        });
      }
    },
  }
};
</script>

<style scoped>
</style>
